import {
  CustomGamesResourceCache,
  ICoordinateSystemInfoProvider,
  SCENE_COMMON_TOKEN,
  SceneCommon,
  SceneFactory,
  T_ICoordinateSystemInfoProvider,
} from '@cgs/common';
import { SpinResponse } from '@cgs/data-access';
import { ApplicationGameConfig, Logger } from '@cgs/shared';
import { Container, IDisposable, JSONResource, ResourcePackage, SceneObject } from '@cgs/wge';
import { AbstractSlotGame } from '../default_game/abstract_slot_game';
import { InitialReelsProvider } from '../initial_reels_provider';
import { ILoadingScreen, LOADING_SCREEN_TOKEN } from '../loading-screen/loading-screen';
import { CHEAT_COMPONENT_TOKEN, CheatComponent } from '../new/cheat';
import {
  SLOT_GAME_CONFIG_MANAGER_TOKEN,
  SlotGameConfigManager,
} from '../new/config/slot-game-config-manager';
import {
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  IGameStateMachineProvider,
} from '../reels_engine/game_components_providers/i_game_state_machine_provider';
import { ISlotGame } from '../reels_engine/i_slot_game';
import { IGameParams } from '../reels_engine/interfaces/i_game_params';
import { GameStateMachine } from '../reels_engine/state_machine/game-state-machine';
import {
  REEL_POSITION_HELPER_TOKEN,
  ReelPositionHelper,
} from '../reels_engine/utils/reel-position.helper';
import { GAME_DATA_SERVICE_TOKEN, GameDataService } from '../services/game-data.service';
import { SLOT_MACHINE_STORE_TOKEN, SlotMachineStore } from '../store';
import {
  RESOURCES_COMPONENT_TOKEN,
  T_AnimationAvailabilityProvider,
  T_AntiCheatProvider,
  T_BalanceListenerProvider,
  T_BaseSlotSoundController,
  T_BigWinPopupProvider,
  T_BonusGameProvider,
  T_BonusRecoveryPopupComponent,
  T_EndBonusGameComponent,
  T_EndFreeSpinTriggerProvider,
  T_EpicWinPopupProvider,
  T_FastSpinDoClickActionProvider,
  T_FreeSpinElemetsProvider,
  T_GameStateMachineNotifierComponent,
  T_IGameConfigProvider,
  T_IInitialReelsProvider,
  T_ILinesModelProvider,
  T_ISlotAnimationController,
  T_ISlotPopupCoordinator,
  T_ISlotPrimaryAnimationProvider,
  T_IWinLineStrategyProvider,
  T_IconModelComponent,
  T_JackPotWinPopupComponent,
  T_LinesSceneObjectComponent,
  T_OptionalLoader,
  T_ProgressiveBonusAnimationProvider,
  T_ProgressiveBreakerProvider,
  T_ScatterGameProvider,
  T_SceneCache,
  T_StickyMaxBetProvider,
  T_TotalWinCoinsProvider,
  T_WinLineActionComponent,
} from '../type_definitions';
import { BaseSlotSoundController } from './common/base_slot_sound_controller';
import { HUD_MODULE_TOKEN, HudModule } from './common/hud-module';
import { DrawOrderConstants } from './common/slot/views/base_popup_view';
import { SlotAnimationController } from './common/slot_animation_controller';
import { SlotPopupCoordinator } from './common/slot_popup_coordinator';
import { SLOT_SESSION_TOKEN, SlotSession } from './common/slot_session';
import { AnimationAvailabilityProvider } from './components/animation_availability_provider';
import { BalanceListenerProvider } from './components/balance_listener_provider';
import { ClientDataRepositoryProvider } from './components/client_data_repository_provider';
import { EndBonusGameComponent } from './components/end_bonus_game_component';
import { EndFreeSpinTriggerProvider } from './components/end_free_spin_trigger_provider';
import { EpicWinPopupProvider } from './components/epic_win/epic_win_popup_provider';
import { FAST_SPINS_COMPONENT, FastSpinsComponent } from './components/fast-spins.component';
import { FastSpinDoClickActionProvider } from './components/fast_spin_do_click_action_provider';
import { FreeSpinElemetsProvider } from './components/freeSpins_logo_component';
import { GameComponentProvider } from './components/game_component_provider';
import { GameConfigComponent } from './components/game_config_component';
import { GameStateMachineNotifierComponent } from './components/game_state_machine_notifier_component';
import { IconModelComponent } from './components/icon_model_component';
import { ISlotPrimaryAnimationProvider } from './components/interfaces/i_slot_primary_animation_provider';
import { LineModelComponent } from './components/line_model_component';
import { LinesSceneObjectComponent } from './components/lines_scene_object_component';
import { BonusGameProvider } from './components/mini_game/bonus_game_provider';
import { ScatterGameProvider } from './components/mini_game/scatter_game_provider';
import { ProgressiveBreakerProvider } from './components/node_tap_action/progressive_breaker/progressive_breaker_provider';
import {
  NOT_ENOUGH_BALANCE_POPUP_COMPONENT_TOKEN,
  NotEnoughBalancePopupComponent,
} from './components/not-enough-balance-popup.component';
import { OptionalLoader } from './components/optional_loader';
import { BigWinPopupProvider } from './components/popups/big_win_popup_provider';
import { BonusRecoveryPopupComponent } from './components/popups/bonus_recovery_popup_component';
import { JackPotWinPopupComponent } from './components/popups/jackpot_win_popup_component';
import { ResourcesComponent } from './components/resources_component';
import { SceneCache } from './components/scene_cache';
import { StickyMaxBetProvider } from './components/sticky_max_bet_provider';
import { T_IStorageRepositoryProvider } from './components/storage-repository.provider';
import { CycledWinLineActionProvider } from './components/win_lines/complex_win_line_action_providers/cycled_win_line_action_provider';
import { RegularWinLineStrategyPrivider } from './components/win_lines/win_line_strategy_providers/regular_win_line_strategy_privider';

export class OverridingComponentProvider {
  factory: any;
  type: any;

  constructor(factory: (container: Container) => any, type: symbol) {
    this.factory = factory;
    this.type = type;
  }
}

export const CUSTOM_COMPONENTS_PROVIDER_TOKEN = Symbol('CUSTOM_COMPONENTS_PROVIDER_TOKEN');
export const REQUIRED_COMPONENTS_PROVIDER_TOKEN = Symbol('REQUIRED_COMPONENTS_PROVIDER_TOKEN');

export class BaseSlotGame extends AbstractSlotGame implements ISlotGame, IDisposable {
  protected _sceneFactory: SceneFactory | null = null;
  protected _hudModule: HudModule | null = null;
  protected readonly _sceneCommon: SceneCommon;

  private readonly _resourceCache: CustomGamesResourceCache | null = null;
  private readonly _coordinateSystemProvider: ICoordinateSystemInfoProvider;
  private readonly _progress: ILoadingScreen;

  private _packages: ResourcePackage[] | null;
  private _linesConfig: Map<any, any> | null;
  private _winLinesConfig: Map<any, any> | null;
  private _symbolsBounds: Map<any, any> | null;
  private _gameConfig: Map<any, any> | null;
  private _slot: SceneObject | null;
  private _bonusHolder: SceneObject | null;
  private _bg: SceneObject | null;

  constructor(container: Container, gameParams: IGameParams) {
    super(gameParams, container);
    this._progress = container.resolve<ILoadingScreen>(LOADING_SCREEN_TOKEN);
    this._coordinateSystemProvider = container.resolve<ICoordinateSystemInfoProvider>(
      T_ICoordinateSystemInfoProvider
    );
    this._sceneCommon = container.resolve<SceneCommon>(SCENE_COMMON_TOKEN);

    this.container.registerValue(
      REQUIRED_COMPONENTS_PROVIDER_TOKEN,
      [
        CHEAT_COMPONENT_TOKEN,
        T_ISlotPopupCoordinator,
        T_TotalWinCoinsProvider,
        T_ProgressiveBreakerProvider,
        T_EndFreeSpinTriggerProvider,
        T_AntiCheatProvider,
        FAST_SPINS_COMPONENT,
        T_FastSpinDoClickActionProvider,
        T_EndBonusGameComponent,
        T_StickyMaxBetProvider,
        T_BalanceListenerProvider,
        T_ISlotAnimationController,
        SLOT_GAME_CONFIG_MANAGER_TOKEN,
        SLOT_SESSION_TOKEN,
      ],
      { multi: true }
    );

    this._resourceCache = this._sceneCommon.resourceCache as CustomGamesResourceCache;
    this._sceneFactory = this._sceneCommon.sceneFactory;
  }

  get winLinesConfig(): Record<string, any> | null {
    return this._winLinesConfig;
  }

  get gameConfig(): Record<string, any> {
    return this._gameConfig!;
  }
  get symbolsBounds(): Record<string, any> {
    return this._symbolsBounds!;
  }
  get linesConfig(): Record<string, any> {
    return this._linesConfig!;
  }

  async initSlotComponents(): Promise<void> {
    this.initComponentContainer();
    this.initCustomComponents();
  }

  public initCustomComponents(): void {
    this.container.resolve(CUSTOM_COMPONENTS_PROVIDER_TOKEN);
    this.container.unregister(CUSTOM_COMPONENTS_PROVIDER_TOKEN);
  }

  public initComponentContainer(): void {
    super.initComponentContainer();

    this.registerAsSingleInstance((container) => {
      Logger.Debug('load ' + 'SlotSession');
      return new SlotSession(container);
    }, SLOT_SESSION_TOKEN);
    this.registerAsSingleInstance((container) => {
      Logger.Debug('load ' + 'ClientDataRepositoryProvider');
      return new ClientDataRepositoryProvider(container);
    }, T_IStorageRepositoryProvider);
    this.registerAsSingleInstance((container) => {
      Logger.Debug('load ' + 'SlotAnimationController');
      return new SlotAnimationController(container);
    }, T_ISlotAnimationController);
    this.registerAsSingleInstance((container) => {
      Logger.Debug('load ' + 'GameConfigProvider');
      return new GameConfigComponent(container, this._coordinateSystemProvider.displayResolution);
    }, T_IGameConfigProvider);
    this.registerAsSingleInstance((container) => {
      Logger.Debug('load ' + 'RegularWinLineStrategyPrivider');
      return new RegularWinLineStrategyPrivider(container);
    }, T_IWinLineStrategyProvider);
    this.registerAsSingleInstance((container) => {
      Logger.Debug('load ' + 'CycledWinLineActionProvider');
      return new CycledWinLineActionProvider(container);
    }, T_WinLineActionComponent);
    this.registerAsSingleInstance((container) => {
      return new LinesSceneObjectComponent(container, this._resourceCache!);
    }, T_LinesSceneObjectComponent);
    this.registerAsSingleInstance((container) => {
      Logger.Debug('load ' + 'ILinesModelProvider');
      return new LineModelComponent(container);
    }, T_ILinesModelProvider);
    this.registerAsSingleInstance((container) => {
      return new CheatComponent(container);
    }, CHEAT_COMPONENT_TOKEN);
    this.registerAsSingleInstance((container) => {
      return new GameStateMachineNotifierComponent(container);
    }, T_GameStateMachineNotifierComponent);
    this.registerAsSingleInstance((container) => {
      Logger.Debug('load ' + 'FreeSpinElemetsProvider');
      return new FreeSpinElemetsProvider(container, []);
    }, T_FreeSpinElemetsProvider);
    this.registerAsSingleInstance((container) => {
      Logger.Debug('load ' + 'JackPotWinPopupComponent');
      return new JackPotWinPopupComponent(container);
    }, T_JackPotWinPopupComponent);
    this.registerAsSingleInstance((container) => {
      return new BigWinPopupProvider(container);
    }, T_BigWinPopupProvider);
    this.registerAsSingleInstance((container) => {
      Logger.Debug('load ' + 'BonusRecoveryPopupComponent');
      return new BonusRecoveryPopupComponent(container);
    }, T_BonusRecoveryPopupComponent);
    this.registerAsSingleInstance((container) => {
      Logger.Debug('load ' + 'BonusGameComponent');
      return new BonusGameProvider(container);
    }, T_BonusGameProvider);
    this.registerAsSingleInstance(() => {
      Logger.Debug('load ' + 'ISlotPopupCoordinator');
      return new SlotPopupCoordinator();
    }, T_ISlotPopupCoordinator);
    this.registerAsSingleInstance((container) => {
      Logger.Debug('load ' + 'OptionalLoader');
      return new OptionalLoader(container);
    }, T_OptionalLoader);
    this.registerAsSingleInstance((container) => {
      Logger.Debug('load ' + 'ProgressiveBreakerProvider');
      return new ProgressiveBreakerProvider(container, [T_ProgressiveBonusAnimationProvider]);
    }, T_ProgressiveBreakerProvider);
    this.registerAsSingleInstance((container) => {
      Logger.Debug('load ' + 'InitialReelsProvider');
      return new InitialReelsProvider(container);
    }, T_IInitialReelsProvider);
    this.registerAsSingleInstance(() => {
      Logger.Debug('load ' + 'SceneCache');
      return new SceneCache(this._sceneCommon.sceneFactory);
    }, T_SceneCache);
    this.registerAsSingleInstance((container) => {
      return new NotEnoughBalancePopupComponent(container);
    }, NOT_ENOUGH_BALANCE_POPUP_COMPONENT_TOKEN);
    this.registerAsSingleInstance((container) => {
      Logger.Debug('load ' + 'EndFreeSpinTriggerProvider');
      return new EndFreeSpinTriggerProvider(container);
    }, T_EndFreeSpinTriggerProvider);
    this.registerAsSingleInstance((container) => {
      Logger.Debug('load ' + 'FastSpinsComponent');
      return new FastSpinsComponent(container);
    }, FAST_SPINS_COMPONENT);
    this.registerAsSingleInstance((container) => {
      Logger.Debug('load ' + 'FastSpinDoClickActionProvider');
      return new FastSpinDoClickActionProvider(container);
    }, T_FastSpinDoClickActionProvider);
    this.registerAsSingleInstance((container) => {
      Logger.Debug('load ' + 'AnimationAvailabilityProvider');
      return new AnimationAvailabilityProvider(container);
    }, T_AnimationAvailabilityProvider);
    this.registerAsSingleInstance((container) => {
      Logger.Debug('load ' + 'EndBonusGameComponent');
      return new EndBonusGameComponent(container);
    }, T_EndBonusGameComponent);
    this.registerAsSingleInstance((container) => {
      Logger.Debug('load ' + 'StickyMaxBetProvider');
      return new StickyMaxBetProvider(container);
    }, T_StickyMaxBetProvider);
    this.registerAsSingleInstance((container) => {
      Logger.Debug('load ' + 'BalanceListenerProvider');
      return new BalanceListenerProvider(container);
    }, T_BalanceListenerProvider);
    this.registerAsSingleInstance(
      (container) => new ReelPositionHelper(container),
      REEL_POSITION_HELPER_TOKEN
    );

    this.registerAsSingleInstance((container) => {
      Logger.Debug('load ' + 'SlotGameConfigManager');
      return new SlotGameConfigManager(container);
    }, SLOT_GAME_CONFIG_MANAGER_TOKEN);
  }

  public async loadResources(): Promise<void> {
    return this._loadResourcesInternal();
  }

  public async startSlotRequest(): Promise<void> {
    const gameDataService = this.container.resolve<GameDataService>(GAME_DATA_SERVICE_TOKEN);
    await gameDataService.startSlot();
  }

  public override async initSlot(): Promise<void> {
    super.initSlot();

    const requireComponents = this.container.resolve<symbol[][]>(
      REQUIRED_COMPONENTS_PROVIDER_TOKEN
    );

    const uniqueRequireComponents = Array.from(new Set(requireComponents.flat()));

    for (const type of uniqueRequireComponents) {
      const provider = this.container.resolve(type, { optional: true });

      if (f_isGameComponentProvider(provider)) {
        provider.initialize();
      }
    }
  }

  public start(): void {
    const slotMachineStore = this.container.resolve<SlotMachineStore>(SLOT_MACHINE_STORE_TOKEN);
    this.gameStateMachine.doStart(slotMachineStore.state);

    const slotSession = this.container.resolve<SlotSession>(SLOT_SESSION_TOKEN);
    slotSession.updateTotalBetEvent();
  }

  private async _loadResourcesInternal(): Promise<void> {
    await this.startSlotRequest();

    const game = `games/${ApplicationGameConfig.gameId}`;

    const resourcesComponent =
      this.container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN);
    this._packages = [];

    const tasks = [
      this.downloadTask(`${game}/slot_bg.zip`),
      this.downloadTaskByUrl('configs/line.zip'),
    ];

    await Promise.all(tasks);

    this._bg = this._sceneFactory?.build('slot_bg/scene') as SceneObject;
    this._bg.id = 'background';
    this._bg.initialize();

    const bgContainer = this._bg.findById('container');
    const bgCommon = this._sceneFactory?.build('slot_bg_common/scene');

    if (bgCommon && bgContainer) {
      bgCommon.initialize();
      bgContainer.addChild(bgCommon);
    }
    resourcesComponent.registerBack(this._bg);
    this._progress.step();
    console.log('slot back loaded!');

    const loadSlotTask = [this.downloadTask(`${game}/slot.zip`)];
    await Promise.all(loadSlotTask);

    this._slot = this._sceneFactory?.build('slot/scene') as SceneObject;
    this._slot.id = 'slot_machine';
    this._slot.initialize();
    this._slot.z = 10;
    this._bonusHolder = new SceneObject();
    this._bonusHolder.position = this._slot.position.clone();
    this._bonusHolder.scale = this._slot.scale.clone();
    this._bonusHolder.z = DrawOrderConstants.MiniGameViewDrawOrder;

    const sounds = this._sceneFactory?.build('slot/sounds') as SceneObject;
    sounds.initialize();

    this._progress.step();
    console.log('slot loaded!');

    resourcesComponent.registerSlot(this._slot, null, 'machine');
    resourcesComponent.registerBonusHolder(this._bonusHolder);
    resourcesComponent.registerSound(sounds);

    if (this._resourceCache?.getResource(JSONResource.TypeId, 'slot/polylineSheet.json')) {
      this._linesConfig = this._resourceCache.getResource(
        JSONResource.TypeId,
        'slot/polylineSheet.json'
      ).data;
    }
    if (this._resourceCache?.getResource(JSONResource.TypeId, 'slot/symbolsBounds.json')) {
      this._symbolsBounds = this._resourceCache.getResource(
        JSONResource.TypeId,
        'slot/symbolsBounds.json'
      ).data;
    }
    if (this._resourceCache?.getResource(JSONResource.TypeId, 'config/lines.json')) {
      this._winLinesConfig = this._resourceCache.getResource(
        JSONResource.TypeId,
        'config/lines.json'
      ).data;
    }

    const gameConfigManager = this.container.resolve<SlotGameConfigManager>(
      SLOT_GAME_CONFIG_MANAGER_TOKEN
    );

    this._gameConfig = await gameConfigManager.loadConfig();

    this._hudModule = this.container.resolve<HudModule>(HUD_MODULE_TOKEN, { optional: true });

    const optionalLoader = this.container.resolve<OptionalLoader>(T_OptionalLoader);
    await optionalLoader.load();

    await this._hudModule?.initialize();

    // тут раньше мы это добавляли в _packages, надо наверное вообще удалить весь код связанный с _packages
    // this._packages.push(footerPackage);

    this._progress.step();
    console.log('hud loaded!');

    await this.loadAdditionalComponents(this._packages);
    this._progress.step();
    console.log('additional loaded!');

    this.container
      .resolve<ISlotPrimaryAnimationProvider>(T_ISlotPrimaryAnimationProvider)
      .attachPrimaryAnimationsToStateMachine();

    this._progress.step();
    console.log('slot components loaded!');

    this.initSlotFinished();
    return Promise.resolve();
  }

  private async downloadTask(path: string): Promise<void> {
    const p = (await this._resourceCache?.loadPackage(path)) as ResourcePackage;
    this._packages?.push(p);
    return Promise.resolve();
  }

  private async downloadTaskByUrl(path: string): Promise<void> {
    const p = (await this._resourceCache?.loadPackageByUrl(path)) as ResourcePackage;
    this._packages?.push(p);
    return Promise.resolve();
  }

  public initSlotFinished(): void {
    this.container.resolve<BaseSlotSoundController>(T_BaseSlotSoundController);
  }

  private async loadAdditionalComponents(_packages: ResourcePackage[]): Promise<void> {
    return Promise.resolve();
  }

  public update(dt: number): void {
    super.update(dt);
  }

  public dispose(): void {
    try {
      const requireComponents = this.container.resolve<symbol[][]>(
        REQUIRED_COMPONENTS_PROVIDER_TOKEN
      );

      const uniqueRequireComponents = Array.from(new Set(requireComponents.flat()));

      for (const componentType of uniqueRequireComponents) {
        const component = this.container.resolve(componentType);
        if (component && f_isGameComponentProvider(component)) {
          component.deinitialize();
        }
      }

      this.container
        .resolve<IGameStateMachineProvider>(GAME_STATE_MACHINE_PROVIDER_TOKEN)
        .deinitialize();
      const res = this.container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN);
      res.unload();
      const cheatComponent = this.container.resolve<CheatComponent>(CHEAT_COMPONENT_TOKEN);
      cheatComponent.dispose();

      if (uniqueRequireComponents.includes(T_BonusGameProvider)) {
        this.container.resolve<BonusGameProvider>(T_BonusGameProvider).dispose();
      }
      if (uniqueRequireComponents.includes(T_ScatterGameProvider)) {
        this.container.resolve<ScatterGameProvider>(T_ScatterGameProvider).dispose();
      }

      this.container.resolve<IconModelComponent>(T_IconModelComponent).dispose();
      this.container.resolve<OptionalLoader>(T_OptionalLoader).dispose();
      this.container.resolve<EpicWinPopupProvider>(T_EpicWinPopupProvider).dispose();

      const slotSession = this.container.resolve<SlotSession>(SLOT_SESSION_TOKEN);
      slotSession.dispose();
    } catch {
      //
    }

    if (this.isInitialized) {
      this.deinitialize();
    }

    if (this._packages) {
      this._packages.forEach((p) => {
        if (p) {
          this._resourceCache?.unloadPackage(p);
        }
      });
      this._packages = null;
    }
  }

  public get gameNode(): AbstractSlotGame {
    return this;
  }

  public getGameStateMachine(): GameStateMachine<SpinResponse> {
    return this.gameStateMachine;
  }

  public get slotLoaded(): Promise<ISlotGame> {
    return this.slotLoadingCompleter.promise;
  }
}

function f_isGameComponentProvider(object: any): object is GameComponentProvider {
  return (
    object &&
    typeof object.getSpecSymbolGroupsByMarker === 'function' &&
    typeof object.initialize === 'function' &&
    typeof object.deinitialize === 'function'
  );
}
